/**
 * SystemAboutPanel — who am I, what am I running, and is it healthy.
 *
 * This content used to exist only inside PopOutGate, which an FSR sees once at
 * launch and never again. When a pilot user reports "it's broken", the first
 * five questions are always which officer, which branch, which build, and
 * whether Core Director and the scanner are up — so the answer lives here, in
 * one component, surfaced from Help → About and echoed subtly in the footer.
 *
 * Bill 2026-09-11: LexisNexis and USPS are shown alongside Core Director. Both
 * are pilot-critical and neither was visible anywhere in the app.
 *
 * 0.156.0: "Software and support dates" - this app's own packages and pcb-retail-api's live
 * GET /api/health/about, each with its end-of-support date (docs/standards/about-dependencies-contract.md).
 * Collapsed by default; the server is asked only when someone opens it.
 */
import { useMemo, useState } from 'react';
import { CheckCircle2, XCircle, HelpCircle, RefreshCw } from 'lucide-react';
import { AboutDependencies, type AboutBackend } from '@retail-ops/about-panel';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useSystemStatus, type StatusValue } from '@/hooks/useSystemStatus';
import { APP_VERSION, APP_BUILD_CHANNEL, APP_BUILD_TIME } from '@/constants/version';
import './SystemAboutPanel.css';

export interface SystemAboutPanelProps {
  /** Teams host detail, when the caller knows it (PopOutGate does). */
  host?: { clientType?: string | null; frameContext?: string | null };
  /** Hide the refresh affordance in embedded/compact contexts. */
  showRefresh?: boolean;
  /** Single-column, tighter spacing — for the user-menu popover. */
  compact?: boolean;
}

function StatusChip({ value, label }: { value: StatusValue; label: string }): JSX.Element {
  // 'unknown' is deliberately NOT red. We did not measure it; that is not the
  // same as it being down, and showing an outage we did not observe sends an
  // FSR chasing a problem that may not exist.
  const Icon = value === 'ok' ? CheckCircle2 : value === 'down' ? XCircle : HelpCircle;
  const text = value === 'ok' ? 'Online' : value === 'down' ? 'Offline' : 'Not checked';
  return (
    <span className={`about-status about-status--${value}`}>
      <Icon size={13} aria-hidden />
      <span className="about-status__text">{text}</span>
      <span className="sr-only">{`${label}: ${text}`}</span>
    </span>
  );
}

export function SystemAboutPanel({ host, showRefresh = true, compact = false }: SystemAboutPanelProps): JSX.Element {
  const { user, authDiag } = useAuth();
  const { config } = useConfig();
  const s = useSystemStatus(true);
  const client = useApiClient();
  const [depsOpen, setDepsOpen] = useState(false);
  const backends = useMemo<AboutBackend[]>(
    () => [{ label: 'Server: pcb-retail-api', load: () => client.health.about() }],
    [client],
  );
  // The one fact the retired auth badge was worth keeping: HOW this session
  // authenticated. Bearer via Teams SSO is the expected answer.
  const authLabel = authDiag?.bearerValid
    ? 'Teams SSO (bearer)'
    : authDiag?.keyPresent
      ? 'Function key (bearer failed)'
      : authDiag?.authMethod ?? 'Not checked';

  const officerName =
    (user?.displayName && user.displayName !== user.upn ? user.displayName : null) ??
    user?.upn ??
    '—';

  // "Branch 01 — Rolla Downtown" reads as a place; "01" reads as a database
  // value. Bill 2026-09-11.
  const branchCode = user?.activeBranchCode ?? user?.branchCode ?? '';
  const branchName = config?.branches?.find((b) => b.branchCode === branchCode)?.branchName ?? '';
  const branchLabel = branchCode
    ? `Branch ${branchCode}${branchName ? ` — ${branchName}` : ''}`
    : '—';

  return (
    <div className={`about-panel${compact ? ' about-panel--compact' : ''}`}>
      <dl className="about-panel__grid">
        <div>
          <dt>Officer</dt>
          <dd>
            <strong>{officerName}</strong>
            {user?.upn && user.upn !== officerName && (
              <span className="about-panel__sub">{user.upn}</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Role</dt>
          <dd>{user?.primaryRole ?? '—'}</dd>
        </div>
        <div>
          <dt>Branch</dt>
          <dd>{branchLabel}</dd>
        </div>
        <div>
          <dt>Officer code</dt>
          <dd>{user?.officerCode ?? '—'}</dd>
        </div>
        <div>
          <dt>Signed in via</dt>
          <dd>{authLabel}</dd>
        </div>
        <div>
          <dt>App</dt>
          <dd>
            v{APP_VERSION} · {APP_BUILD_CHANNEL}
          </dd>
        </div>
        <div>
          <dt>Core Director</dt>
          <dd><StatusChip value={s.coreDirector} label="Core Director" /></dd>
        </div>
        <div>
          <dt>LexisNexis</dt>
          <dd><StatusChip value={s.lexisNexis} label="LexisNexis" /></dd>
        </div>
        <div>
          <dt>USPS address</dt>
          <dd><StatusChip value={s.usps} label="USPS" /></dd>
        </div>
        <div>
          <dt>Scanner Bridge</dt>
          <dd><StatusChip value={s.scannerBridge} label="Scanner Bridge" /></dd>
        </div>
        {host && (
          <div>
            <dt>Host</dt>
            <dd>
              Teams {host.clientType ?? 'unknown'}
              <span className="about-panel__sub">{host.frameContext ?? '—'}</span>
            </dd>
          </div>
        )}
        <div>
          <dt>Built</dt>
          <dd className="about-panel__sub">{APP_BUILD_TIME}</dd>
        </div>
      </dl>

      <details
        className="about-panel__deps"
        onToggle={(e) => setDepsOpen((e.currentTarget as HTMLDetailsElement).open)}
      >
        <summary>Software and support dates</summary>
        {depsOpen && (
          <div className="about-panel__deps-body">
            <AboutDependencies compact={compact} backends={backends} />
          </div>
        )}
      </details>

      {showRefresh && (
        <div className="about-panel__actions">
          <button type="button" className="about-panel__refresh" onClick={s.refresh} disabled={s.loading}>
            <RefreshCw size={13} aria-hidden />
            {s.loading ? 'Checking…' : 'Re-check now'}
          </button>
          <span className="about-panel__hint">
            Include this panel in a screenshot when you report a problem.
          </span>
        </div>
      )}
    </div>
  );
}

export default SystemAboutPanel;
